// SCR-CONSOLE-007 — اطلاعیه‌ها (US-PLT-082): اطلاعیه درون‌برنامه‌ای با مخاطب (همه، پلن، مستأجر خاص؛ تخصص در فاز 1b —
// C-AGENT-424)، نقش‌های هدف، بازه نمایش و سطح اهمیت؛ شمار تأیید مشاهده و بایگانی.
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type AnnouncementAudience,
  type AnnouncementSeverity,
  type AnnouncementView,
} from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  ListPage,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { consoleKeys, toNumber } from '../../api';

export const SCR_CONSOLE_007 = 'SCR-CONSOLE-007';

const SEVERITIES: readonly AnnouncementSeverity[] = ['Info', 'Warning', 'Critical'];
const AUDIENCES: readonly AnnouncementAudience[] = ['All', 'Plan', 'Tenant'];

function splitList(value: string): string[] {
  return value
    .split(/[,،\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** مقدار datetime-local (زمان محلی مرورگر) ← ISO UTC. */
function toIso(local: string): string | null {
  if (!local) {
    return null;
  }
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function AnnouncementDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [severity, setSeverity] = useState<AnnouncementSeverity>('Info');
  const [audience, setAudience] = useState<AnnouncementAudience>('All');
  const [keys, setKeys] = useState('');
  const [roles, setRoles] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [touched, setTouched] = useState(false);

  const start = toIso(startsAt);
  const end = toIso(endsAt);
  const errors = {
    title: !title.trim(),
    body: !body.trim(),
    keys: audience !== 'All' && splitList(keys).length === 0,
    window: !start || !end || end <= start,
  };
  const invalid = Object.values(errors).some(Boolean);

  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/console/announcements', {
          body: {
            title: title.trim(),
            body: body.trim(),
            severity,
            audience,
            audienceKeys: audience === 'All' ? [] : splitList(keys),
            targetRoles: splitList(roles),
            startsAt: start,
            endsAt: end,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.announcements });
      onClose();
    },
  });

  return (
    <FormDialog
      open
      titleKey="console:announcements.create_title"
      screenId={SCR_CONSOLE_007}
      onSubmit={() => {
        setTouched(true);
        if (!invalid) {
          create.mutate();
        }
      }}
      onClose={onClose}
      isDirty={Boolean(title || body)}
      isSubmitting={create.isPending}
      submitError={create.error ?? undefined}
      maxWidth="md"
    >
      <TextField
        label={t('console:announcements.title_field')}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        required
        fullWidth
        error={touched && errors.title}
        helperText={touched && errors.title ? t('console:validation.required') : undefined}
        slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'announcement-title' } }}
      />
      <TextField
        label={t('console:announcements.body')}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        required
        fullWidth
        multiline
        minRows={3}
        error={touched && errors.body}
        helperText={touched && errors.body ? t('console:validation.required') : undefined}
        slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': 'announcement-body' } }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          select
          label={t('console:announcements.severity')}
          value={severity}
          onChange={(event) => setSeverity(event.target.value as AnnouncementSeverity)}
          fullWidth
        >
          {SEVERITIES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`console:announcements.severities.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label={t('console:announcements.audience')}
          value={audience}
          onChange={(event) => setAudience(event.target.value as AnnouncementAudience)}
          fullWidth
          data-testid="announcement-audience"
        >
          {AUDIENCES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`console:announcements.audiences.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {audience !== 'All' && (
        <TextField
          label={t(`console:announcements.keys_${audience}`)}
          value={keys}
          onChange={(event) => setKeys(event.target.value)}
          fullWidth
          error={touched && errors.keys}
          helperText={
            touched && errors.keys ? t('console:validation.required') : t('console:announcements.keys_help')
          }
          slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'announcement-keys' } }}
        />
      )}
      <TextField
        label={t('console:announcements.roles')}
        value={roles}
        onChange={(event) => setRoles(event.target.value)}
        fullWidth
        helperText={t('console:announcements.roles_help')}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'announcement-roles' } }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          type="datetime-local"
          label={t('console:announcements.starts_at')}
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
          fullWidth
          error={touched && errors.window}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'announcement-starts' } }}
        />
        <TextField
          type="datetime-local"
          label={t('console:announcements.ends_at')}
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
          fullWidth
          error={touched && errors.window}
          helperText={touched && errors.window ? t('console:announcements.window_invalid') : undefined}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'announcement-ends' } }}
        />
      </Stack>
    </FormDialog>
  );
}

export function AnnouncementsPage() {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [archived, setArchived] = useState(false);
  const [archiving, setArchiving] = useState<AnnouncementView | null>(null);

  const announcements = useQuery({
    queryKey: [...consoleKeys.announcements, archived],
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/announcements', {
          params: { query: { includeArchived: archived } },
        }),
      ),
  });
  const archive = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/v1/platform/console/announcements/{id}:archive', { params: { path: { id } } })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: consoleKeys.announcements });
      setArchiving(null);
    },
  });

  const columns: DataTableColumn<AnnouncementView>[] = [
    { id: 'title', headerKey: 'console:announcements.title_field', cell: (row) => row.title },
    {
      id: 'severity',
      headerKey: 'console:announcements.severity',
      cell: (row) => (
        <Chip
          size="small"
          color={row.severity === 'Critical' ? 'error' : row.severity === 'Warning' ? 'warning' : 'info'}
          label={t(`console:announcements.severities.${row.severity}`)}
        />
      ),
    },
    {
      id: 'audience',
      headerKey: 'console:announcements.audience',
      cell: (row) =>
        t(`console:announcements.audiences.${row.audience}`) +
        (row.audienceKeys.length > 0 ? ` (${row.audienceKeys.join(', ')})` : ''),
    },
    {
      id: 'window',
      headerKey: 'console:announcements.window',
      cell: (row) => (
        <>
          <JalaliDate value={row.startsAt} withTime /> – <JalaliDate value={row.endsAt} withTime />
        </>
      ),
    },
    {
      id: 'acks',
      headerKey: 'console:announcements.acks',
      cell: (row) => (
        <span data-testid={`announcement-acks-${row.id}`}>{faDigits(toNumber(row.ackCount) ?? 0)}</span>
      ),
    },
    {
      id: 'actions',
      headerKey: 'console:announcements.actions',
      cell: (row) =>
        row.isArchived ? (
          <Chip size="small" label={t('console:announcements.archived')} />
        ) : (
          <Button
            size="small"
            onClick={() => setArchiving(row)}
            data-testid={`announcement-archive-${row.id}`}
          >
            {t('console:announcements.archive')}
          </Button>
        ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_CONSOLE_007}
      titleKey="console:announcements.title"
      subtitleKey="console:announcements.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          data-testid="announcement-create"
        >
          {t('console:announcements.create')}
        </Button>
      }
      filters={
        <FormControlLabel
          control={<Switch checked={archived} onChange={(event) => setArchived(event.target.checked)} />}
          label={t('console:announcements.include_archived')}
        />
      }
    >
      <DataTable
        captionKey="console:announcements.caption"
        columns={columns}
        rows={announcements.data?.items}
        getRowId={(row) => row.id}
        isLoading={announcements.isPending}
        error={announcements.error}
        onRetry={() => void announcements.refetch()}
        emptyTitleKey="console:announcements.empty"
      />
      {creating && <AnnouncementDialog onClose={() => setCreating(false)} />}
      <ConfirmDialog
        open={archiving !== null}
        titleKey="console:announcements.archive_title"
        messageKey="console:announcements.archive_message"
        messageValues={{ title: archiving?.title ?? '' }}
        busy={archive.isPending}
        error={archive.error}
        onConfirm={() => archiving && archive.mutate(archiving.id)}
        onCancel={() => setArchiving(null)}
      />
    </ListPage>
  );
}
